import type { Orient } from '@photos/shared';
import { usePhotos, type PhotoQuery } from '../api/hooks';
import { Hero } from '../components/Hero';
import { Icon, type IconName } from '../components/Icon';
import { Lightbox } from '../components/Lightbox';
import { PhotoGrid } from '../components/PhotoGrid';
import { SelectionBar } from '../components/SelectionBar';
import { usePhotoGridState } from '../hooks/usePhotoGridState';
import { fmtCount, fmtTotal, fmtRangeIso } from '../lib/format';
import { useLibraryState, type OrientFilter } from '../state/libraryParams';
import { useSession } from '../state/session';

export const ORIENT: { key: OrientFilter; label: string; short: string; icon?: IconName }[] = [
  { key: 'all', label: 'All shapes', short: 'All' },
  { key: 'h', label: 'Horizontal', short: 'Horizontal', icon: 'shapeH' },
  { key: 'v', label: 'Vertical', short: 'Vertical', icon: 'shapeV' },
  { key: 'sq', label: 'Square', short: 'Square', icon: 'shapeSq' },
];

/** All photos / Picks / tag results: banner, narrow-down row, day-grouped grid, selection, lightbox. */
export function ResultsView() {
  const { state, go } = useLibraryState();
  const { tagIndex, me } = useSession();
  const query: PhotoQuery = {
    source: state.source,
    tags: state.tags,
    picksOnly: state.tab === 'picks' || state.picksOnly,
    orient: state.orient === 'all' ? undefined : (state.orient as Orient),
  };
  const queryKey = JSON.stringify(query);
  const photos = usePhotos(query);
  const first = photos.data?.pages[0];
  const { items, selected, selectedPhotos, clear, onTileClick, onSelectDay, loadMore, selectAll, selectingAll, lbIndex, setLbIndex } =
    usePhotoGridState(photos, queryKey);

  const total = first?.total ?? 0;
  const oc = first?.orientCounts ?? { h: 0, v: 0, sq: 0 };
  const orientCount = (k: OrientFilter) => (k === 'all' ? oc.h + oc.v + oc.sq : oc[k]);
  const tagTitle = state.tags.map((t) => tagIndex.labelOf(t)).join(' · ');
  const title = state.tab === 'picks' ? 'Picks' : tagTitle || 'All photos';
  const span = fmtRangeIso(first?.firstTakenAt, first?.lastTakenAt);
  const sub = first ? [`${fmtCount(total)} photos`, ...(span ? [span] : []), fmtTotal(first.totalBytes)] : [];
  const cover = items.find((p) => p.picked) ?? items[0];
  const related = (first?.related ?? []).filter((r) => !state.tags.includes(r.tag) && r.count < total).slice(0, 10);

  const actions = (
    <>
      <div className="seg" role="radiogroup" aria-label="Photo shape">
        {ORIENT.filter((o) => o.key === 'all' || orientCount(o.key) || state.orient === o.key).map((o) => (
          <button
            type="button"
            key={o.key}
            role="radio"
            aria-checked={state.orient === o.key}
            className={state.orient === o.key ? 'on' : ''}
            title={`${o.label} · ${orientCount(o.key)} photos`}
            onClick={() => go({ orient: o.key })}
          >
            {o.icon && <Icon name={o.icon} />}
            <span>{o.short}</span>
          </button>
        ))}
      </div>
      {state.tab !== 'picks' && (
        <label className="toggle">
          <input type="checkbox" id="picksOnly" checked={state.picksOnly} onChange={(e) => go({ picksOnly: e.target.checked })} /> ★
          Picks only
        </label>
      )}
      {total > 0 && (
        <button type="button" className="btn sm" id="selAll" onClick={selectAll} disabled={selectingAll}>
          <Icon name="check" />
          Select all
        </button>
      )}
    </>
  );

  const layoutKey = [title, ...sub, state.orient, state.picksOnly, state.tab, total > 0, oc.h > 0, oc.v > 0, oc.sq > 0].join('|');

  return (
    <>
      <Hero title={title} sub={sub} coverUrl={cover?.thumbUrl} actions={actions} layoutKey={layoutKey} />
      {state.source === 'flock' && !state.tags.length && state.tab === 'all' && (
        <div className="notice">
          <Icon name="flock" />
          <span>
            Photos members posted in the <b>Flock</b> app. They use the same tags, so Years, Regions, Events, Churches,
            Groups, Picks and search all work the same way.{' '}
            {me.capabilities.media && (
              <>
                Select photos and choose <b>Add to Glean</b> to keep the best ones in Glean, the official collection.
              </>
            )}
          </span>
        </div>
      )}
      {related.length > 0 && state.tags.length > 0 && (
        <div className="related">
          <span className="lead">Narrow down:</span>
          {related.map((r) => (
            <button type="button" key={r.tag} onClick={() => go({ tags: [...state.tags, r.tag] })}>
              #{r.tag} <span style={{ color: 'var(--muted)' }}>{r.count}</span>
            </button>
          ))}
        </div>
      )}
      {photos.isError ? (
        <div className="empty">
          <h3>Couldn’t load photos</h3>
          {photos.error.message}
        </div>
      ) : first && !items.length ? (
        <div id="grid">
          <div className="empty">
            <h3>No photos match</h3>
            Try removing a tag.
          </div>
        </div>
      ) : !first ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : (
        <PhotoGrid
          items={items}
          total={total}
          selected={selected}
          source={state.source}
          hasMore={!!photos.hasNextPage}
          loadMore={loadMore}
          onTileClick={onTileClick}
          onSelectDay={onSelectDay}
        />
      )}
      <SelectionBar photos={selectedPhotos} onClear={clear} />
      {lbIndex >= 0 && (
        <Lightbox
          photos={items}
          total={total}
          index={lbIndex}
          onIndex={setLbIndex}
          onClose={() => setLbIndex(-1)}
          onNearEnd={loadMore}
          onSearchTag={(t) => {
            setLbIndex(-1);
            go({ tab: 'all', tags: [t] });
          }}
        />
      )}
    </>
  );
}
